import { useFormikContext } from 'formik';
import { useContext } from 'react';

import { FormikCheckbox } from '../../../../cdl/Checkbox/FormikCheckbox';
import { Text } from '../../../../cdl/Text/Text';
import { translate } from '../../../../common/helpers/translate.helper';
import { InfoPopover } from '../../../../common/InfoPopover/InfoPopover';
import { Box } from '../../../../common/ui/Box';

import { ContinueButton } from './ContinueButton';
import { CreateWebhookContext } from './CreateWebhookContext';
import { CreateWebhookFormState, CreateWebhookFormSteps } from './CreateWebhookModalContent';
import { WebhookFormButtonArea } from './WebhookFormButtonArea';

export const FormikWebhookSelectEvents = () => {
    const { values, setValues, setFieldValue } = useFormikContext<CreateWebhookFormState>();
    const { setActiveFormStep } = useContext(CreateWebhookContext);

    const handleEventChange = (field: 'draftEnquiry' | 'enquiry' | 'offer' | 'approvalRequest', checked: boolean) => {
        const updatedResourceTypes = {
            ...values.resourceTypes,
            [field]: checked,
        };

        const allSelected = Object.values(updatedResourceTypes).every(Boolean);

        setFieldValue('allEventsSelected', allSelected, false);
    };

    const handleAllChange = (checked: boolean) => {
        if (checked) {
            setValues(
                {
                    ...values,
                    allEventsSelected: true,
                    resourceTypes: {
                        draftEnquiry: true,
                        enquiry: true,
                        offer: true,
                        approvalRequest: true,
                    },
                },
                false
            );
        } else {
            setValues(
                {
                    ...values,
                    allEventsSelected: false,
                    resourceTypes: {
                        draftEnquiry: false,
                        enquiry: false,
                        offer: false,
                        approvalRequest: false,
                    },
                },
                false
            );
        }
    };

    return (
        <Box display="grid" gap={16}>
            <Box display="grid" gap={8}>
                <Text variant="title">{translate('webhooks.selectEvents.title')}</Text>
                <Text variant="small">{translate('webhooks.selectEvents.description')}</Text>
            </Box>

            <Box>
                <Box display="flex" justifyContent="flex-start" alignItems="center" gap={4} color="foreground.subtle">
                    <FormikCheckbox
                        onChange={handleAllChange}
                        name="allEventsSelected"
                        label={translate('webhooks.selectEvents.allEvents')}
                    />
                    <InfoPopover width="280px" side="right">
                        <Text variant="extraSmall">{translate('webhooks.selectEvents.allEvents.popover')}</Text>
                    </InfoPopover>
                </Box>

                <Box marginY={16}>
                    <Text variant="fieldLabel" color="foreground.muted" marginTop={16}>
                        {translate('webhooks.selectEvents.eventGroups')}
                    </Text>
                    <Box display="flex" flexDirection="column" rowGap={8} marginTop={4} alignItems="flex-start">
                        <FormikCheckbox
                            name="resourceTypes.draftEnquiry"
                            label={translate('webhooks.selectEvents.draftEnquiry.title')}
                            description={translate('webhooks.selectEvents.draftEnquiry.description')}
                            onChange={(checked) => handleEventChange('draftEnquiry', checked)}
                        />
                        <FormikCheckbox
                            name="resourceTypes.enquiry"
                            label={translate('webhooks.selectEvents.enquiry.title')}
                            description={translate('webhooks.selectEvents.enquiry.description')}
                            onChange={(checked) => handleEventChange('enquiry', checked)}
                        />
                        <FormikCheckbox
                            name="resourceTypes.offer"
                            label={translate('webhooks.selectEvents.offer.title')}
                            description={translate('webhooks.selectEvents.offer.description')}
                            onChange={(checked) => handleEventChange('offer', checked)}
                        />
                        <FormikCheckbox
                            name="resourceTypes.approvalRequest"
                            label={translate('webhooks.selectEvents.approvalRequest.title')}
                            description={translate('webhooks.selectEvents.approvalRequest.description')}
                            onChange={(checked) => handleEventChange('approvalRequest', checked)}
                        />
                    </Box>
                </Box>
            </Box>

            <WebhookFormButtonArea>
                <ContinueButton
                    type="button"
                    disabled={
                        !values.resourceTypes.offer &&
                        !values.resourceTypes.draftEnquiry &&
                        !values.resourceTypes.enquiry &&
                        !values.resourceTypes.approvalRequest
                    }
                    onClick={() => {
                        setActiveFormStep(CreateWebhookFormSteps.DECIDE_TRIGGERS);
                    }}
                />
            </WebhookFormButtonArea>
        </Box>
    );
};
